import { Injectable } from '@angular/core';
import { FollowsRetroplannings } from '@app/models/retroplanning';
import { RetroplanningService } from '@app/services/api/retroplanning.service';
import { FlashService } from '@app/services/flash.service';
import {
  RetroplanningActionTypes,
  AddAll,
  AddOne,
  Create,
  CreateFail,
  CreateSuccess,
  ExportRetroplanning,
  Load,
  LoadFail,
  LoadSuccess,
  LoadSingle,
  LoadSingleFail,
  LoadSingleSuccess,
  GetRetroplanningsLink,
  GetRetroplanningsLinkFail,
  Update,
  UpdateFail,
  UpdateSuccess,
  UpdateOne,
  UploadRetroplanning,
  UploadRetroplanningFail,
  UploadRetroplanningSucces,
  DeleteRetroplanning,
  DeleteRetroplanningSuccess,
  UploadProcessUpdate,
  UploadProcessSuccess,
  UploadProcessFail
} from '@core/actions/retroplanning.actions';

import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  exhaustMap,
  concatMap
} from 'rxjs/operators';
import { ApiService } from '@app/services/api/api.service';
import { WebsocketService } from '@app/services/websocket.service';
import { Store } from '@ngrx/store';

@Injectable()
export class RetroplanningEffects {
  @Effect()
  load$: Observable<Action> = this.actions$.pipe(
    ofType<Load>(RetroplanningActionTypes.Load),
    switchMap(id => {
      return this.retroplanningApi.get().pipe(
        map((retroplannings: any[]) => new LoadSuccess(retroplannings)),
        catchError(error => of(new LoadFail(error)))
      );
    })
  );

  @Effect()
  loadSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<LoadSuccess>(RetroplanningActionTypes.LoadSuccess),
    map(action => new AddAll(action.data))
  );

  @Effect()
  loadOne$: Observable<Action> = this.actions$.pipe(
    ofType<LoadSingle>(RetroplanningActionTypes.LoadSingle),
    map(action => action.id),
    switchMap(id => {
      return this.retroplanningApi.getOne(id).pipe(
        map(retroplannings => new LoadSingleSuccess(retroplannings)),
        catchError(error => of(new LoadSingleFail(error)))
      );
    })
  );

  @Effect()
  LoadSingleSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<LoadSingleSuccess>(RetroplanningActionTypes.LoadSingleSuccess),
    map(action => new AddOne(action.data))
  );

  @Effect()
  create$: Observable<Action> = this.actions$.pipe(
    ofType<Create>(RetroplanningActionTypes.Create),
    switchMap(action =>
      this.retroplanningApi.create(action.followId, action.retroplanning).pipe(
        // map((savedColor: Color) => new CreateSuccess(savedColor)),
        map(
          (savedColor: FollowsRetroplannings) => new CreateSuccess(savedColor)
        ),
        catchError(error => of(new CreateFail(error)))
      )
    )
  );

  @Effect({ dispatch: false })
  createFailFail$ = this.actions$.pipe(
    ofType<CreateFail>(RetroplanningActionTypes.CreateFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        message: 'No se pudo crear el retroplanning',
        type: 'danger'
      })
    )
  );

  @Effect()
  createSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<CreateSuccess>(RetroplanningActionTypes.CreateSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Retroplanning creado' });
      return new AddOne(action.retroplanning);
    })
  );

  @Effect()
  deleteColor$: Observable<Action> = this.actions$.pipe(
    ofType<DeleteRetroplanning>(
      RetroplanningActionTypes.DeleteRetroplanningSuccess
    ),
    map(action => [action.idRetroplaning, action.followId]),
    switchMap(([idRetroplaning, followId]) => {
      return this.retroplanningApi
        .delete(idRetroplaning, followId)
        .pipe(map(() => new DeleteRetroplanningSuccess(followId)));
    })
  );

  @Effect()
  DeleteColorSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<DeleteRetroplanningSuccess>(
      RetroplanningActionTypes.DeleteRetroplanningSuccess
    ),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Retroplanning Eliminado' });
      // return new Load(action.followId);
      return new Load();
    })
  );

  @Effect()
  update$: Observable<Action> = this.actions$.pipe(
    ofType<Update>(RetroplanningActionTypes.Update),
    map(action => [action.followId, action.data]),
    switchMap(([followId, data]) => {
      return this.retroplanningApi.update(followId, data).pipe(
        map(
          (retroplanning: FollowsRetroplannings) =>
            new UpdateSuccess(retroplanning.id, retroplanning)
        ),
        catchError(error => of(new UpdateFail(error)))
      );
    })
  );

  @Effect()
  updateSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateSuccess>(RetroplanningActionTypes.UpdateSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Retroplanning actualizado' });
      return new UpdateOne(action.data);
    })
    // map(action => {
    //   this.fs.emit({ type: 'fail', message: 'No se pudo actualizar'});
    //   return new UpdateFail(action.color);
    // })
  );

  @Effect()
  UpdateSuccess$ = this.actions$.pipe(
    ofType<UpdateOne>(RetroplanningActionTypes.UpdateOne),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Retroplannings actualizados' });
      // return new Load(action.retroplanning.FollowId);
      return new Load();
    })
  );

  @Effect({ dispatch: false })
  updateFail$ = this.actions$.pipe(
    ofType<UpdateFail>(RetroplanningActionTypes.UpdateFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        message: 'Retroplanning no actualizado',
        type: 'danger'
      })
    )
  );

  @Effect()
  uploadRetroplanning$ = this.actions$.pipe(
    ofType<UploadRetroplanning>(RetroplanningActionTypes.UploadRetroplanning),
    map(action => [action.uploadUrl, action.FileId, action.file]),
    switchMap(([uploadUrl, FileId, file]) => {
      return this.api.uploadFile(uploadUrl, file).pipe(
        map(response => {
          return new UploadRetroplanningSucces(FileId);
        }),
        catchError(e => {
          return of(new UploadRetroplanningFail(e));
        })
      );
    })
  );

  @Effect()
  uploadRetroplanningSucces$ = this.actions$.pipe(
    ofType<UploadRetroplanningSucces>(
      RetroplanningActionTypes.UploadRetroplanningSucces
    ),
    map(action => action.FileId),
    switchMap((FileId: number) => {
      return this.api
        .notifyFileUpload(FileId, { processType: 'Retroplanning' })
        .pipe(
          map(action => {
            return new UploadProcessUpdate({
              status: 'pending',
              message: 'Procesando retroplanning...'
            });
          }),
          // delay(2000),
          catchError(e => of(new UploadProcessFail(e)))
        );
    })
  );

  @Effect()
  uploadRetroplanningFail$ = this.actions$.pipe(
    ofType<UploadRetroplanningFail>(
      RetroplanningActionTypes.UploadRetroplanningFail
    ),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        message: 'No se pudo cargar el retroplanning',
        type: 'danger'
      })
    )
  );

  @Effect()
  uploadProcessSuccess$ = this.actions$.pipe(
    ofType<UploadProcessSuccess>(RetroplanningActionTypes.UploadProcessSuccess),
    map(data => {
      this.fs.emit({ type: 'success', message: 'Retroplanning actualizado' });
      return new Load();
    })
  );

  @Effect()
  UploadProcessFail$ = this.actions$.pipe(
    ofType<UploadProcessFail>(RetroplanningActionTypes.UploadProcessFail),
    map(action => {
      if (action.error.message) {
        this.fs.emit({ type: 'danger', message: action.error.message });
      } else {
        this.fs.emit({
          type: 'danger',
          message: 'No se pudo cargar el retroplanning'
        });
      }
    })
  );

  /*********/
  @Effect()
  getRetroplanningLink$ = this.actions$.pipe(
    ofType<GetRetroplanningsLink>(
      RetroplanningActionTypes.GetRetroplanningsLink
    ),
    map(action => [action.file, action.followId]),
    switchMap(([file, followId]) => {
      return this.api.getRetroplanningUploadLink(followId, file).pipe(
        map((record: any) => {
          return new UploadRetroplanning(record.signedUrl, record.id, file);
        }),
        catchError(e => of(new GetRetroplanningsLinkFail(e)))
      );
    })
  );

  @Effect({ dispatch: false })
  getRetroplanningLinkFail$ = this.actions$.pipe(
    ofType<GetRetroplanningsLinkFail>(
      RetroplanningActionTypes.GetRetroplanningsLinkFail
    ),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        type: 'danger',
        message: 'Error al cargar Retroplanning'
      })
    )
  );

  /*********/

  constructor(
    private actions$: Actions,
    private retroplanningApi: RetroplanningService,
    private fs: FlashService,
    private api: ApiService,
    private ws: WebsocketService,
    private store: Store<any>
  ) {}
}
